import React, { useCallback, useContext } from 'react';

import { useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import Lottie from 'react-lottie-player';
import { Link } from 'react-router-dom';

import ModalRequestQuotation from 'Components/ModalRequestQuotation';
import Icon from 'Components/Icon';
import AmountPaid from 'Components/AmountPaid/AmountPaid';
import TabQuotationsTable from './components/TabQuotationsTable/TabQuotationsTable';
import animationData from 'Shared/spiner_blue.json';
import getTotalAmountPaid from 'Shared/util/getTotalAmountPaid';

import emptyLogo from '../../assets/QuatationsEmpty.svg';
import emptyLogoDark from '../../assets/QuatationsEmptyDark.svg';
import EmptyPage from 'Components/EmptyPage/EmptyPage';

import StoreContext from 'Shared/context/StoreContext';
import useQueryByContract from 'Shared/hooks/useQueryByContract';
import withErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';
import { logger } from 'RootShared/utility/ErrorLogger';
import useDocumentTitle from 'Shared/hooks/useDocumentTitle';

export const queryGetQuotationsOrders = gql`
  query TabQuotationsQuery($contractId: Int!) @api(name: root) {
    orders(status: SENT, contractId: $contractId) {
      id
      reference
      subject
      createdAt
      total
      pdfUrl
      currency
    }
  }
`;

export const mutationApproveQuote = gql`
  mutation approveQuoteMutation($orderId: Int!) @api(name: root) {
    approveQuote(orderId: $orderId) {
      order {
        id
        reference
      }
    }
  }
`;

const toastParams = {
  autoClose: 3000,
  closeButton: true,
  hideProgressBar: false,
};

const TabQuotations = () => {
  const { contract, refetchIndication } = useContext(StoreContext);
  const { loading, data, error } = useQueryByContract(contract, queryGetQuotationsOrders);

  useDocumentTitle('Orders - Quotations');

  const onSentEmailMutationError = useCallback((error) => {
    logger.error(error);
    toast(error.message, {
      ...toastParams,
      type: toast.TYPE.ERROR,
    });
  }, []);

  const onSentEmailMutationSuccess = useCallback(async () => {
    toast('Quotation approved successfully', {
      ...toastParams,
      type: toast.TYPE.SUCCESS,
    });
    refetchIndication && (await refetchIndication());
  }, [refetchIndication]);

  const [approveQuoteMutation] = useMutation(mutationApproveQuote, {
    onCompleted: onSentEmailMutationSuccess,
    onError: onSentEmailMutationError,
    update(cache, { data: { approveQuote } }) {
      if (approveQuote) {
        const arr = [...data.orders];
        const ordersUpdate = arr.filter((item) => item.id !== approveQuote.order.id);
        cache.modify({
          fields: {
            orders() {
              return ordersUpdate;
            },
          },
        });
      }
    },
  });

  const formatingForTable = useCallback((orders) => {
    let ordersForTable = [];

    ordersForTable = orders.map(({ reference, subject, createdAt, total, currency, pdfUrl, id }) => {
      return {
        order: { reference: reference, slug: subject },
        issued: { date: createdAt },
        total: { number: total, currency: currency },
        download: { url: pdfUrl },
        id: id,
      };
    });

    return ordersForTable;
  }, []);

  const getOrdersComponent = useCallback(
    (data) => {
      if (data.orders.length === 0) {
        return (
          <div data-testid="tab-quotations-empty-page">
            <EmptyPage
              img={
                <img src={JSON.parse(localStorage.getItem('nightMode')) ? emptyLogoDark : emptyLogo} alt="empty logo" />
              }
              title={'No quotations yet'}
              text={"Looks like you haven't had quotations yet."}
            />
          </div>
        );
      }

      return (
        <>
          <AmountPaid title={'Total amount to be paid'} amount={getTotalAmountPaid(data.orders)} />
          <TabQuotationsTable orders={formatingForTable(data.orders)} approveQuoteMutation={approveQuoteMutation} />
        </>
      );
    },
    [approveQuoteMutation, formatingForTable]
  );

  if (error) {
    logger.error(error);
    toast(error.message, {
      type: toast.TYPE.ERROR,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }

  return (
    <>
      <div className="container orders" data-testid="tab-quotations">
        <div className="tab-quotations">
          <div className="tab-group-control-header">
            <Link
              className="tab-group-control-header__btn tab-group-control-header__btn--contained"
              to="/customer/orders/quotations/?request_quotation"
            >
              <Icon name="icon-plus" />
              <span data-testid="request-quotation">Request a quotation</span>
            </Link>
          </div>
          {loading && (
            <Lottie
              data-testid="tab_expenses_history"
              className="lottie"
              loop={true}
              play={true}
              animationData={animationData}
              width={100}
              style={{ width: 100, height: 100, margin: '0px auto' }}
            />
          )}
          {data && getOrdersComponent(data)}
        </div>
      </div>
      <ModalRequestQuotation />
    </>
  );
};

export default withErrorBoundary(TabQuotations);
